import { useState, useEffect, useRef } from "react";
import { useAppDispatch } from "../store";
import { getValidSasToken, fetchSasToken } from "../store/documentsSlice";
import styled from "styled-components";
import { delay } from "../utils";
import { Button, Typography, Spin } from "antd";
import axios from "axios";

type Props = {
  src: string;
  envConfig: any;
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  position: relative;
  background: #303030;
`;

const Embed = styled.embed`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  object-fit: contain;
  position: absolute;
  background: #303030;
`;

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  position: relative;
  background: #303030;
  z-index: 9999;
`;

const TransparentOverlay = styled.div`
  position: absolute;
  z-index: 9999;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  background: #000;
`;

const DownloadContainer = styled.div`
  width: 200px;
  text-align: center;
  align-self: center;
`;

const unsupportedExtensions = [".doc", ".docx", ".xls", ".xlsx", ".csv", ".mov", ".mp4"];

const axiosWithoutAuth = axios.create();

export default function DocumentRenderer({ src, envConfig }: Props) {
  const [overlay, setOverlay] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [embedSrc, setEmbedSrc] = useState<string | null>(null); // State to manage the embed source
  const embedSrcRef = useRef<string | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const encodeLink = (link: string, sasToken: string) => {
      return `https://${envConfig.appConfig.storageAccountName}.blob.core.windows.net/dropzonehistory/` + encodeURI(link) + `?` + sasToken;
    };

    const fetchAndSetEmbedSrc = async () => {
      if (!src) return;

      try {
        // Ensure the SAS token is valid
        const validSasToken = await dispatch(getValidSasToken()).unwrap();
        const encodedSrc = encodeLink(src, validSasToken);

        // Fetch the resource and create a Blob URL
        const response = await axiosWithoutAuth.get(encodedSrc, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(response.data);

        setEmbedSrc(blobUrl);
        embedSrcRef.current = blobUrl;
      } catch (error) {
        // Refresh the SAS token
        const controller = new AbortController();
        const refreshedSasToken = await dispatch(fetchSasToken({ controller })).unwrap();
        controller.abort();

        // Retry with the new token
        const refreshedEncodedSrc = encodeLink(src, refreshedSasToken);
        try {
          const refreshedResponse = await axiosWithoutAuth.get(refreshedEncodedSrc, { responseType: "blob" });
          const refreshedBlobUrl = URL.createObjectURL(refreshedResponse.data);

          setEmbedSrc(refreshedBlobUrl);
          embedSrcRef.current = refreshedBlobUrl;
        } catch (retryError) {
          console.error("Failed to fetch and set embed source after retry:", retryError);
          setEmbedSrc(null);
          embedSrcRef.current = null;
        }
      }
      finally {
        setIsLoading(false);
      }
    };

    fetchAndSetEmbedSrc();

    // Cleanup Blob URL when the component unmounts or embedSrc changes
    return () => {
      if (embedSrcRef.current) {
        URL.revokeObjectURL(embedSrcRef.current);
        embedSrcRef.current = null;
      }
    };
  }, [envConfig.appConfig.storageAccountName, src, dispatch]);

  const hideOverlay = async () => {
    await delay(220);
    setOverlay(false);
  };

  const displayOverlay = () => {
    setOverlay(true);
  };

  if (!embedSrc && !isLoading) {
    return (
      <Container>
        <Typography.Text strong italic style={{ color: "white" }}>
          Failed to load the document. Please reload the page or try again later.
        </Typography.Text>
      </Container>
    );
  }

  if (embedSrc && src && unsupportedExtensions.some((extension) => src.endsWith(extension))) {
    return (
      <Container>
        <DownloadContainer>
          <Typography.Text strong italic style={{ color: "white" }}>
            Unsupported document type.
          </Typography.Text>

          <Button
            style={{ width: "100%", marginTop: 10 }}
            onClick={() => {
              const iframe = document.createElement("iframe");
              iframe.style.display = "none";
              iframe.src = embedSrc;
              document.body.appendChild(iframe);
              iframe.onload = () => {
                document.body.removeChild(iframe);
              };
            }}
          >
            Download document
          </Button>
        </DownloadContainer>
      </Container>
    );
  }

  return (
    <Container onMouseLeave={displayOverlay}>
      <Embed
        onError={() => {
          console.error("Failed to load embed source.");
          setEmbedSrc(null);
          embedSrcRef.current = null;
          setIsLoading(false);
        }}
        src={
          embedSrc ||
          (src?.endsWith(".pdf") ? "" : "data:image/gif;base64,R0lGODlhAQABAIAAAP///AMDAwAAACwAAAAAAQABAAACAkQBADs=")
        }
      />
      {isLoading && (
        <Overlay>
          <Spin size="large" className="white-spin" />
        </Overlay>
      )}
      {overlay ? <TransparentOverlay onMouseEnter={hideOverlay} onClick={hideOverlay}></TransparentOverlay> : null}
    </Container>
  );
}
